/* eslint-disable complexity */
import React from 'react';
import { Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as Icon from 'react-bootstrap-icons';

import { Grid } from '../../../common/components/Grid/Grid';
import {
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import momencik from '../../../common/momencik';
import { GetExamDto } from '../../../common/spdCore/autogenerated/spdApiClient';

import { ExamExternalReferralDeleteLink } from './ExamExternalReferralDeleteLink';
import { ExternalReferralPreviewLink } from './ExternalReferralPreviewLink';

interface ExternalReferralSearchProps {
  getData: GridGetterFunction;
  getCount: GridCountFunction;
  refresh?: boolean;
  initialOrderColumn?: string;
  initialOrderDirection?: 'DESC' | 'ASC';
  onRowClick?: (key: string) => Promise<void> | void | undefined;
  onlyExternalRegistration?: boolean;
}

export const externalReferralSearchForGrid = (
  exams: GetExamDto,
  setRefresh?: () => void | undefined,
  useOfficialName?: boolean,
) => {
  return {
    key: exams.id,
    values: {
      'exams.number': (
        <>
          <Badge bg="info" className="me-1">
            {exams.number}
          </Badge>
        </>
      ),
      'exams.examined': exams.examined && (
        <>
          {exams.examined.surname} {exams.examined.firstname}
        </>
      ),
      'examined.pesel': exams?.examined?.pesel || '',
      'examined.email': exams?.examined?.email || '',
      'exams.registrationDate':
        momencik(exams.registrationDate, 'YYYY-MM-DD HH:mm:ss') || '',
      'exams.referralDate': momencik(exams.referralDate, 'YYYY-MM-DD') || '',
      'creator.name': exams?.creator?.name || '',
      'creator.email': exams?.creator?.email || '',
      employmentPositionFieldOfStudy:
        exams.fieldOfStudy || exams.employmentPosition,
      schoolNameEmployerName: exams.schoolName || exams.employerName,
      status: !exams.startDate
        ? 'Oczekiwanie'
        : exams.endDate
          ? 'Zakończono'
          : 'W trakcie',
      'exams.pin': (
        <OverlayTrigger
          placement="bottom"
          overlay={<Tooltip>{exams.pin}</Tooltip>}
        >
          <Badge
            bg="success"
            className="me-1"
            onClick={e => e.stopPropagation()}
          >
            #
          </Badge>
        </OverlayTrigger>
      ),
      critical_point: exams?.criticalPoint && (
        <OverlayTrigger
          placement="bottom"
          overlay={
            <Tooltip>
              Zostały oznaczone punkty krytyczne: {exams.criticalPoint}
            </Tooltip>
          }
        >
          <Badge
            bg="danger"
            className="me-1"
            onClick={e => e.stopPropagation()}
          >
            !
          </Badge>
        </OverlayTrigger>
      ),
      'healthcareFacility.displayName':
        exams?.examPackage?.healthcareFacility?.displayName || '',
      'company.name': useOfficialName
        ? exams?.examPackage?.company?.officialName ||
          exams?.examPackage?.company?.name ||
          ''
        : exams?.examPackage?.company?.name || '',
      'exams.endDate': (
        <>
          {exams.endVersion && (
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip>Wersja stanowiska testów: {exams.endVersion}</Tooltip>
              }
            >
              <Icon.InfoCircle />
            </OverlayTrigger>
          )}{' '}
          {momencik(exams.endDate, 'YYYY-MM-DD HH:mm:ss')}
        </>
      ),
      preview: (
        <>
          <ExternalReferralPreviewLink examId={exams.id} />
        </>
      ),
      bin: (
        <>
          {!exams.endDate && !exams.startDate && (
            <ExamExternalReferralDeleteLink
              examId={exams.id}
              setRefresh={setRefresh}
            />
          )}
        </>
      ),
    },
  };
};

export const ExternalReferralSearch: React.FC<
  ExternalReferralSearchProps
> = props => {
  const columns = [
    ...(props.onlyExternalRegistration
      ? []
      : [{ header: '', property: 'critical_point' }]),
    { header: 'Numer badania', property: 'exams.number' },
    ...(props.onlyExternalRegistration
      ? []
      : [{ header: 'Pin', property: 'exams.pin' }]),
    { header: 'Badany', property: 'exams.examined' },
    { header: 'PESEL', property: 'examined.pesel' },
    { header: 'Email', property: 'examined.email' },
    ...(!props.onlyExternalRegistration
      ? []
      : [
          {
            header: 'Stanowisko',
            property: 'employmentPositionFieldOfStudy',
            noSorting: true,
            noFilter: true,
          },
        ]),
    { header: 'Data rejestracji', property: 'exams.registrationDate' },
    { header: 'Data skierowania', property: 'exams.referralDate' },
    { header: 'Osoba rejestrująca', property: 'creator.name' },
    { header: 'E-mail rejestrującego', property: 'creator.email' },
    { header: 'Firma rejestrująca', property: 'company.name' },
    ...(props.onlyExternalRegistration
      ? []
      : [{ header: 'Placówka', property: 'healthcareFacility.displayName' }]),
    ...(props.onlyExternalRegistration
      ? []
      : [{ header: 'Koniec', property: 'exams.endDate' }]),
    {
      header: 'Status badania',
      property: 'status',
      noSorting: true,
      noFilter: true,
    },
    { header: '', property: 'preview', noSorting: true, noFilter: true },
    { header: '', property: 'bin', noSorting: true, noFilter: true },
  ];

  return (
    <Grid
      getData={props.getData}
      getDataCount={props.getCount}
      columns={columns}
      showFilter={true}
      withHistory={true}
      initialOrderColumn={props.initialOrderColumn || 'exams.number'}
      initialOrderDirection={props.initialOrderDirection || 'DESC'}
      availablePageSizes={[10, 20, 50]}
      emptyGridMessage="Brak wystawionych skierowań zewnętrznych w systemie"
      onRowClick={props.onRowClick}
      refresh={props.refresh}
    />
  );
};
